import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { MONTH_ABBR } from '../../constants';

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth(); // 0-indexed

const monthStart = (m) => new Date(CURRENT_YEAR, m, 1);
const monthEnd = (m) => new Date(CURRENT_YEAR, m + 1, 0, 23, 59, 59);
const toISO = (d) => d.toISOString().split('T')[0];
const yearMin = `${CURRENT_YEAR}-01-01`;
const yearMax = `${CURRENT_YEAR}-12-31`;

/**
 * Emits { startDate, endDate } (ISO date strings) on every change.
 * Defaults to the current calendar month on mount.
 */
const DashboardDateFilter = ({ onChange }) => {
  const { dark } = useTheme();
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH); // null = custom
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const selectMonth = (mIdx) => {
    setSelectedMonth(mIdx);
    setShowCustom(false);
    onChange({
      startDate: toISO(monthStart(mIdx)),
      endDate: toISO(monthEnd(mIdx)),
    });
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    // Clamp both dates within current year
    const from = customFrom < yearMin ? yearMin : customFrom;
    const to = customTo > yearMax ? yearMax : customTo;
    setSelectedMonth(null);
    onChange({ startDate: from, endDate: to });
  };

  const inputStyle = {
    padding: '6px 10px',
    borderRadius: 7,
    fontSize: 12,
    border: `1px solid ${dark ? '#334155' : '#D1D5DB'}`,
    background: dark ? '#1E293B' : '#F8FAFC',
    color: dark ? '#F1F5F9' : '#0F172A',
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {/* Month chips */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {MONTH_ABBR.map((abbr, i) => {
          const active = selectedMonth === i;
          const isFuture = i > CURRENT_MONTH;
          return (
            <button
              key={i}
              onClick={() => !isFuture && selectMonth(i)}
              disabled={isFuture}
              style={{
                padding: '4px 10px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                border: `1px solid ${active ? '#3B82F6' : (dark ? '#334155' : '#E2E8F0')}`,
                background: active ? '#3B82F6' : 'transparent',
                color: active ? '#fff' : isFuture
                  ? (dark ? '#334155' : '#D1D5DB')
                  : (dark ? '#94A3B8' : '#475569'),
                cursor: isFuture ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {abbr}
            </button>
          );
        })}
      </div>

      {/* Custom range toggle */}
      <button
        onClick={() => setShowCustom(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500,
          border: `1px solid ${showCustom ? '#8B5CF6' : (dark ? '#334155' : '#E2E8F0')}`,
          background: showCustom ? (dark ? '#2D1B69' : '#EDE9FE') : 'transparent',
          color: showCustom ? '#8B5CF6' : (dark ? '#94A3B8' : '#475569'),
          cursor: 'pointer',
        }}
      >
        <CalendarDays size={13} />
        Custom
      </button>

      {/* Custom range inputs */}
      {showCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <input
            type="date"
            value={customFrom}
            min={yearMin}
            max={customTo || yearMax}
            onChange={e => setCustomFrom(e.target.value)}
            style={inputStyle}
          />
          <span style={{ fontSize: 12, color: dark ? '#475569' : '#94A3B8' }}>to</span>
          <input
            type="date"
            value={customTo}
            min={customFrom || yearMin}
            max={yearMax}
            onChange={e => setCustomTo(e.target.value)}
            style={inputStyle}
          />
          <button
            onClick={applyCustom}
            disabled={!customFrom || !customTo}
            style={{
              padding: '5px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
              background: customFrom && customTo ? '#3B82F6' : (dark ? '#1E293B' : '#E2E8F0'),
              color: customFrom && customTo ? '#fff' : (dark ? '#334155' : '#94A3B8'),
              border: 'none', cursor: customFrom && customTo ? 'pointer' : 'not-allowed',
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardDateFilter;
